import { useState } from 'react';
import { api } from '../api';
import {
  ASSESSMENT_SECTIONS,
  DOCUMENT_LABELS,
  QUALIFICATION_LABELS,
  SUPPLIER_ASSESSMENT_QUESTIONS,
  SupplierAssessmentApplicabilityService,
  supplierAnswerSchema,
  type AssessmentIssue,
  type SupplierAssessmentDraft,
  type SupplierDocumentView,
  type SupplierDocumentType,
  type SupplierQualification,
} from '../shared/supplier-assessment';

export function SupplierAssessmentFields({
  step,
  draft,
  categories,
  documents,
  onChange,
  onUpload,
  disabled,
  issues = [],
}: {
  step: number;
  draft: SupplierAssessmentDraft;
  categories: string[];
  documents: SupplierDocumentView[];
  onChange: (draft: SupplierAssessmentDraft) => void;
  onUpload: (
    type: SupplierDocumentType,
    file: File,
    issue: string,
    expiry: string,
  ) => Promise<void>;
  disabled: boolean;
  issues?: AssessmentIssue[];
}) {
  const [dates, setDates] = useState<Record<string, { issue: string; expiry: string }>>({});
  const q = draft.qualification;
  const rules = new SupplierAssessmentApplicabilityService(q, categories, draft.answers);
  const update = (key: keyof SupplierQualification, value: unknown) =>
    onChange({ ...draft, qualification: { ...q, [key]: value } });
  const input = (
    key: keyof SupplierQualification,
    label: string,
    required = false,
    type = 'text',
  ) => (
    <label className="block text-sm font-semibold" id={`assessment-qualification.${key}`}>
      {label}
      {required && <span className="text-rose-600"> *</span>}
      <input
        className="mt-2 block w-full rounded-xl border border-slate-200 p-3 font-normal"
        type={type}
        value={String(q[key])}
        disabled={disabled}
        onChange={(e) => update(key, e.target.value)}
      />
      {issues
        .filter((issue) => issue.path === `qualification.${key}`)
        .map((issue) => (
          <span key={issue.path} className="block text-rose-600">
            {issue.message}
          </span>
        ))}
    </label>
  );
  const radio = (
    key: keyof SupplierQualification,
    label: string,
    options: Record<string, string>,
    required = false,
  ) => (
    <fieldset className="space-y-2" id={`assessment-qualification.${key}`}>
      <legend className="mb-2 text-sm font-semibold">
        {label}
        {required && <span className="text-rose-600"> *</span>}
      </legend>
      <div className="grid gap-2 sm:grid-cols-3">
        {Object.entries(options).map(([value, text]) => (
          <label
            key={value}
            className={`flex cursor-pointer items-center gap-2 rounded-xl border p-3 text-sm transition ${
              q[key] === value
                ? 'border-teal-400 bg-teal-50 font-bold text-teal-800 shadow-xs'
                : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
            }`}
          >
            <input
              type="radio"
              name={`assessment-qualification-${key}`}
              disabled={disabled}
              checked={q[key] === value}
              onChange={() => update(key, value)}
              className="accent-teal-600"
            />
            {text}
          </label>
        ))}
      </div>
      {issues
        .filter((issue) => issue.path === `qualification.${key}`)
        .map((issue) => (
          <span key={issue.path} className="block text-sm text-rose-600">
            {issue.message}
          </span>
        ))}
    </fieldset>
  );
  const multi = (
    key: keyof SupplierQualification,
    label: string,
    options: Record<string, string>,
  ) => (
    <fieldset className="space-y-2" id={`assessment-qualification.${key}`}>
      <legend className="mb-2 font-semibold">{label}（可複選）</legend>
      <div className="grid gap-2 sm:grid-cols-2">
        {Object.entries(options).map(([value, text]) => {
          const values = q[key] as string[];
          return (
            <label
              key={value}
              className="flex items-center gap-3 rounded-xl bg-slate-50 p-3 text-sm"
            >
              <input
                type="checkbox"
                disabled={disabled}
                checked={values.includes(value)}
                onChange={(e) =>
                  update(
                    key,
                    e.target.checked ? [...values, value] : values.filter((v) => v !== value),
                  )
                }
              />
              {text}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
  const document = (type: SupplierDocumentType, isQuestion = false) => {
    const matching = documents.filter((doc) => doc.document_type === type);
    const hasUploaded = matching.length > 0;
    return (
      <div
        className="mt-3 rounded-xl border border-dashed border-slate-300 p-4"
        id={`assessment-documents.${type}`}
      >
        <div className="flex items-center justify-between">
          <p className="font-semibold text-ink">{DOCUMENT_LABELS[type]}</p>
          {hasUploaded && (
            <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-bold text-emerald-800">
              ✓ 已上傳
            </span>
          )}
        </div>
        {matching.map((doc) => (
          <div key={doc.id} className="mt-2 flex flex-wrap items-center gap-2 text-sm text-teal-800">
            <span>{isQuestion ? '✓ 已於「供應商資格」上傳：' : '✓ 已上傳，可供資格及問卷共同引用：'}</span>
            <a
              className="font-bold underline hover:text-teal-950"
              href={api.vendorProactive.assessmentDocumentUrl(doc.id)}
              target="_blank"
              rel="noreferrer"
            >
              {doc.file_name}（查看）
            </a>
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
              {
                {
                  VALID: '有效',
                  EXPIRING_SOON: '即將到期',
                  EXPIRED: '已過期',
                  UNKNOWN: '未設定效期',
                }[doc.status]
              }
            </span>
          </div>
        ))}
        <div className="my-3 grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            發證日期
            <input
              className="ml-2 rounded border p-2"
              type="date"
              disabled={disabled}
              value={dates[type]?.issue ?? ''}
              onChange={(e) =>
                setDates((v) => ({
                  ...v,
                  [type]: { issue: e.target.value, expiry: v[type]?.expiry ?? '' },
                }))
              }
            />
          </label>
          <label className="text-sm">
            有效日期
            <input
              className="ml-2 rounded border p-2"
              type="date"
              disabled={disabled}
              value={dates[type]?.expiry ?? ''}
              onChange={(e) =>
                setDates((v) => ({
                  ...v,
                  [type]: { issue: v[type]?.issue ?? '', expiry: e.target.value },
                }))
              }
            />
          </label>
        </div>
        <input
          aria-label={`上傳${DOCUMENT_LABELS[type]}`}
          type="file"
          disabled={disabled}
          accept=".pdf,.jpg,.jpeg,.png,.webp"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file)
              void onUpload(type, file, dates[type]?.issue ?? '', dates[type]?.expiry ?? '');
            e.target.value = '';
          }}
        />
        <p className="mt-2 text-xs text-slate-500">
          支援 PDF、JPG、PNG、WEBP；上限依系統設定。補充文件不會刪除舊檔。
        </p>
      </div>
    );
  };
  return (
    <div className="space-y-5">
      {step === 1 && input('responsible_person', '公司負責人', true)}
      {step === 2 && (
        <>
          {multi('supplier_qualification_types', '供應商資格類型 *', QUALIFICATION_LABELS)}
          {q.supplier_qualification_types.includes('OTHER') &&
            input('qualification_other', '其他供應商資格說明', true)}
          {q.supplier_qualification_types.includes('DRUG_DEALER') && (
            <>
              {input('drug_dealer_license_number', '藥商許可執照字號', true)}
              {document('DRUG_DEALER_LICENSE')}
            </>
          )}
          {q.supplier_qualification_types.includes('DRUG_MANUFACTURER') && (
            <>
              {input('drug_manufacturer_license_number', '藥廠許可／製造相關字號', true)}
              {document('DRUG_MANUFACTURER_LICENSE')}
            </>
          )}
          <label className="flex gap-3">
            <input
              type="checkbox"
              checked={q.has_pharmaceutical_transport}
              disabled={disabled}
              onChange={(e) => update('has_pharmaceutical_transport', e.target.checked)}
            />
            涉及藥品配送／運輸
          </label>
          {rules.pharmaceutical &&
            radio(
              'gdp_status',
              '【GDP狀態】',
              {
                COMPLETED: '已完成',
                NOT_COMPLETED: '未完成',
                NOT_APPLICABLE: '不適用',
              },
              true,
            )}
          {q.gdp_status === 'COMPLETED' && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                {input('gdp_issue_date', 'GDP發證日期', false, 'date')}
                {input('gdp_expiry_date', 'GDP有效日期', false, 'date')}
              </div>
              {multi('gdp_approved_scopes', 'GDP核准範圍', {
                STORAGE: '儲存',
                TRANSPORT: '運輸',
                OTHER: '其他',
              })}
              {document('GDP_APPROVAL')}
            </>
          )}
        </>
      )}
      {step === 3 && input('contact_line', 'LINE')}
      {step === 4 &&
        (rules.pharmaceutical || q.supplier_qualification_types.includes('TRANSPORTER')) && (
          <>
            <h3 className="font-bold text-ink text-base">【藥品運輸資料】</h3>
            {radio(
              'transport_responsibility',
              '運輸責任者',
              {
                SUPPLIER: '供應商',
                THIRD_PARTY: '第三方物流',
                OTHER: '其他',
              },
              true,
            )}
            {['THIRD_PARTY', 'OTHER'].includes(q.transport_responsibility) &&
              input('transport_company_name', '配送運輸業者名稱', true)}
            {multi('transport_temperature_conditions', '運送溫度', {
              COLD_2_8: '2～8℃',
              CONTROLLED_15_25: '15～25℃',
              BELOW_25: '25℃以下',
              BELOW_30: '30℃以下',
              AMBIENT: '常溫',
              OTHER: '其他',
            })}
            {q.transport_temperature_conditions.includes('OTHER') &&
              input('transport_temperature_other', '其他運送溫度', true)}
            {multi('transport_methods', '運輸方式', {
              A: '空調車',
              B: '非空調車',
              C: '確效包裝',
              D: '連續溫度記錄',
              E: '溫度偏離警報',
              F: '其他',
            })}
            {q.transport_methods.includes('F') &&
              input('transport_notes', '其他運輸方式說明', true)}
          </>
        )}
      {step === 5 && (
        <>
          {!rules.pharmaceutical && (
            <p className="rounded-xl bg-sky-50 p-4 text-sky-900">
              依目前供應商類型，不需要填寫完整藥品GDP評估問卷；請完成下列一般資格及聲明。
            </p>
          )}
          {rules.pharmaceutical && (
            <>
              {radio(
                'warehouse_type',
                '倉儲方式',
                {
                  OWN: '自有倉儲',
                  OUTSOURCED: '委外倉儲',
                  BOTH: '兩者皆有',
                },
                true,
              )}
              {['OUTSOURCED', 'BOTH'].includes(q.warehouse_type) &&
                input('warehouse_company_name', '委外倉儲公司名稱', true)}
            </>
          )}
          {Object.entries(ASSESSMENT_SECTIONS).map(([code, title]) => {
            const questions = SUPPLIER_ASSESSMENT_QUESTIONS.filter(
              (item) => item.section_code === code && rules.question(item) !== 'NOT_APPLICABLE',
            );
            const required = questions.filter((item) => rules.question(item) === 'REQUIRED');
            if (!questions.length)
              return (
                <div
                  key={code}
                  className="flex items-center justify-between rounded-xl bg-slate-50 p-4 text-slate-500"
                >
                  <span className="font-bold">
                    【{code} {title}】
                  </span>
                  <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-bold text-slate-600">
                    不適用
                  </span>
                </div>
              );
            const answeredCount = required.filter(
              (item) => draft.answers[item.question_code]?.answer,
            ).length;
            const isCompleted = answeredCount === required.length;
            return (
              <details
                key={code}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-xs"
                open={
                  issues.some((issue) => issue.path.startsWith(`answers.${code}.`)) || undefined
                }
              >
                <summary className="flex cursor-pointer items-center justify-between font-bold text-ink hover:text-teal-700">
                  <span className="text-base">
                    【{code} {title}】（{questions.length} 題）
                  </span>
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-bold ${
                      isCompleted
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    完成 {answeredCount}/{required.length}
                  </span>
                </summary>
                {code === '1' && (
                  <p className="my-3 text-sm text-amber-800">資格審查：必要文件，不予計分。</p>
                )}
                {questions.map((item) => {
                  const answer =
                    draft.answers[item.question_code] ?? supplierAnswerSchema.parse({});
                  const patch = (next: Partial<typeof answer>) =>
                    onChange({
                      ...draft,
                      answers: { ...draft.answers, [item.question_code]: { ...answer, ...next } },
                    });
                  return (
                    <fieldset
                      key={item.question_code}
                      id={`assessment-answers.${item.question_code}`}
                      className="my-4 rounded-xl bg-slate-50 p-4"
                    >
                      <legend className="font-semibold text-ink">
                        {item.question_code} {item.question_text}
                        {rules.question(item) === 'REQUIRED' && (
                          <span className="text-rose-600"> *</span>
                        )}
                      </legend>
                      <div className="my-3 flex flex-wrap gap-3">
                        {Object.entries({
                          YES: '是',
                          NO: '否',
                          NOT_APPLICABLE: '不適用',
                        } as const).map(([value, text]) => (
                          <label
                            key={value}
                            className={`flex cursor-pointer items-center gap-2 rounded-xl border px-4 py-3 text-sm transition ${
                              answer.answer === value
                                ? 'border-teal-400 bg-teal-50 font-bold text-teal-800 shadow-xs'
                                : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-100'
                            }`}
                          >
                            <input
                              type="radio"
                              name={`assessment-${item.question_code}`}
                              disabled={disabled}
                              checked={answer.answer === value}
                              onChange={() => patch({ answer: value as typeof answer.answer })}
                              className="accent-teal-600"
                            />
                            {text}
                          </label>
                        ))}
                      </div>
                      <label className="text-sm">
                        補充說明
                        <textarea
                          className="mt-2 block w-full rounded-xl border border-slate-200 bg-white p-3 text-sm"
                          value={answer.comment}
                          disabled={disabled}
                          placeholder="如有相關補充說明請在此填寫"
                          onChange={(e) => patch({ comment: e.target.value })}
                        />
                      </label>
                      {answer.answer === 'YES' && item.requires_description && (
                        <label className="mt-3 block text-sm font-semibold">
                          證照／認證名稱 *
                          <input
                            className="mt-1 block w-full rounded-xl border border-slate-200 p-2.5 font-normal"
                            disabled={disabled}
                            value={answer.certification_name}
                            placeholder="請填寫證照／認證完整名稱"
                            onChange={(e) => patch({ certification_name: e.target.value })}
                          />
                        </label>
                      )}
                      {answer.answer === 'YES' &&
                        item.required_document_type &&
                        document(item.required_document_type, true)}
                      {issues
                        .filter((issue) => issue.path.startsWith(`answers.${item.question_code}`))
                        .map((issue) => (
                          <p key={issue.path} className="mt-2 text-sm text-rose-600">
                            {issue.message}
                          </p>
                        ))}
                    </fieldset>
                  );
                })}
              </details>
            );
          })}
        </>
      )}
      {step === 7 && (
        <>
          <h3 className="font-bold">供應商評估文件</h3>
          {Object.keys(DOCUMENT_LABELS).map((type) => (
            <details key={type} className="rounded-xl border p-3">
              <summary>
                {DOCUMENT_LABELS[type as SupplierDocumentType]}（
                {documents.filter((doc) => doc.document_type === type).length} 份）
              </summary>
              {document(type as SupplierDocumentType)}
            </details>
          ))}
          <div className="grid gap-4 sm:grid-cols-2">
            {input('filled_by_name', '填表人姓名', true)}
            {input('filled_by_title', '填表人職稱')}
            {input('filled_by_email', '填表人Email', false, 'email')}
          </div>
          <label
            id="assessment-qualification.declaration_accepted"
            className="flex gap-3 rounded-xl bg-amber-50 p-4"
          >
            <input
              type="checkbox"
              disabled={disabled}
              checked={q.declaration_accepted}
              onChange={(e) => update('declaration_accepted', e.target.checked)}
            />
            本人確認以上資料均由本公司提供，並確認內容真實且完整。 *
          </label>
        </>
      )}
    </div>
  );
}
